<template lang="pug">
.change-status-modal
  b-modal(v-model="_value" :size="size" @ok="ok" @cancel="cancel" no-close-on-backdrop no-close-on-esc)
    div(slot="modal-title")
      span {{ title }}
    
    .d-flex.flex-column
      .d-flex.flex-row.align-items-center.mb-2
        span 現在のステップ：
        span {{ convertStatusName(currentStatus) }}
      .d-flex.flex-row.align-items-center.mb-2
        span 変更後のステップ：
        b-form-select.status-select(v-model="_changedStatus" :options="options" style="width: 280px;")
      .error-messages
        .error-message {{ errorMessage }}

    .d-flex.flex-column.mb-2(v-if="0 < targetFacilities.length && changedStatus != 100")
      span(style="font-weight: bold;") 複数施設に申し込んでいる場合、他施設のステップを同じステップに変更できます。
      .warning-message 同時にステップ変更したい施設にチェックを入れてください。
      .mb-2
      .facilities
        .facility(v-for="(facility, index) in targetFacilities")
          b-form-checkbox(v-model="targetFacilities[index].checked" :value="true" :unchecked-value="false") {{ facility.name }}

    .register-body.d-flex.flex-column.mb-2(v-if="changedStatus == 100")
      span(style="font-weight: bold;") 利用者情報を入力してください。
      .mb-2
      .resident-input
        FormInputSelect(v-model="roomId" name="room-id" key-name="部屋" :options="roomOptions" :validation-rules="{ required: true }"
          :styles="{ 'max-width': '540px' }" required)
        FormInputSelect(v-model="groupId" name="group-id" key-name="利用者グループ" :options="residentGroupOptions" :validation-rules="{ required: true }"
          :styles="{ 'max-width': '540px' }" required)
        FormInput(v-model="insuredNumber" key-name="保険者番号" name="insured-number" type="text" placeholder="保険者番号"
          :maxlength="6" :validation-rules="{ length: 6, max: 6, custom_numeric: true }" :styles="{ width: '180px' }")
        FormInput(v-model="careInsuranceInsuredNumber" key-name="介護保険被保険者番号" name="care-insurance-insured-number"
          type="text" placeholder="介護保険被保険者番号"
          :maxlength="10" :validation-rules="{ length: 10, max: 10, half_alpha_num: true }" :styles="{ width: '320px' }")
        FormDatetimePicker(v-model="inDatetime" :date.sync="inDatetimeDate" :time.sync="inDatetimeTime" name="in-date" key-name="入所日／入所予定日"
          placeholder="入所日／入所予定日")
        FormTimePicker(v-if="inDatetime" v-model="pickUpDatetime" :time.sync="pickUpDatetimeTime" name="pick-up-time" key-name="入所送迎時間" 
          placeholder="入所送迎時間")
        FormDatetimePicker(v-model="outDatetime" :date.sync="outDatetimeDate" :time.sync="outDatetimeTime" name="out-date" key-name="退所日／退所予定日"
          placeholder="退所日／退所予定日")
        FormTimePicker(v-if="outDatetime" v-model="dropOffDatetime" :time.sync="dropOffDatetimeTime" name="drop-off-time" key-name="退所送迎時間"
          placeholder="退所送迎時間")
    .d-flex.flex-column.mb-2(v-if="0 < targetFacilities.length && changedStatus == 100")
      span(style="font-weight: bold;") 複数施設に申し込んでいる場合、他施設の申込情報を取り下げに変更できます。
      .warning-message 取り下げに変更したい施設にチェックを入れてください。
      .mb-2
      .facilities
        .facility(v-for="(facility, index) in targetFacilities")
          b-form-checkbox(v-model="targetFacilities[index].checked" :value="true" :unchecked-value="false") {{ facility.name }}

    .register-body.d-flex.flex-column.mb-2(v-if="changedStatus == 5")
      span(style="font-weight: bold;") 取下げ理由を選択・入力してください。
      .mb-2
      .cancel-reason-input
        FormInputSelect(v-model="cancelReason" name="cancel-reason" key-name="取下げ理由" :options="cancelReasonOptions" :validation-rules="{ required: true }"
          :styles="{ 'max-width': '540px' }" required)
        FormInput(v-if="cancelReason == 'other'" v-model="cancelReasonText" key-name="取下げ理由（自由記述）" name="cancel-reason-text" type="text" placeholder="取下げ理由を入力"
          :maxlength="100" :validation-rules="{ max: 100, required: true }" :styles="{ width: '540px' }" required)

    .error-messages(v-if="statusErrorMessage")
        .error-message {{ statusErrorMessage }}

    template(#modal-footer="{ ok, cancel }" style="width:100%;")
      ValidationAlert(v-model="showValidationAlert")
      .buttons.d-flex.justify-content-end
        button.btn.modal-ok-btn(type="button" @click="ok()") {{ okButtonLabel }}
        button.btn.modal-cancel-btn(type="button" @click="cancel()") {{ cancelButtonLabel }}
</template>

<script lang="ts">
import { Component, Prop, Inject, Vue } from "vue-property-decorator";
import { userStore } from "@/store/store";
import CustomValidate from "@/commons/utils/CustomValidate";

import { ResidentStatus } from "@/commons/services/Residents";
import RoomsService, { RoomGetModel, RoomType, UseType } from "@/commons/services/Rooms";
import ResidentGroupsService, { ResidentGroupsGetModel } from "@/commons/services/ResidentGroups";
import { UpdateResidentModel } from "@/commons/services/Waiters";

import ValidationAlert from "@/pc/basics/ValidationAlertByValidator.vue";
import FormInput from "@/pc/components/forms/FormInput.vue";
import FormInputSelect from "@/pc/components/forms/FormInputSelect.vue";
import FormDatetimePicker from "@/pc/components/forms/FormDatetimePicker.vue";
import FormTimePicker from "@/pc/components/forms/FormTimePicker.vue";

/**
 * ### OK/キャンセルモーダル
 */
@Component({
  components: {
    ValidationAlert,
    FormInput,
    FormInputSelect,
    FormDatetimePicker,
    FormTimePicker
  },
  $_veeValidate: { validator: "new" }
})
export default class ChangeStatusModal extends Vue {
  /** 部屋サービス */
  @Inject("RoomsService") roomsService!: RoomsService;
  /** 利用者グループサービス */
  @Inject("ResidentGroupsService") residentGroupsService!: ResidentGroupsService;

  /** 表示可否 */
  @Prop({ required: true })
  value!: boolean;
  /** タイトル */
  @Prop({ required: true })
  title!: string;
  /** モーダルサイズ */
  @Prop({ default: () => "lg" })
  size!: string;
  /** OKボタンラベル */
  @Prop({ default: () => "更新" })
  okButtonLabel!: string;
  /** キャンセルボタンラベル */
  @Prop({ default: () => "キャンセル" })
  cancelButtonLabel!: string;

  /** 現在の状態 */
  @Prop()
  currentStatus!: ResidentStatus;
  /** 変更後の状態 */
  @Prop()
  changedStatus!: ResidentStatus;
  /** ステータス変更によるエラーメッセージ */
  @Prop()
  statusErrorMessage!: string;

  /** 取り下げ理由（自由記述） */
  @Prop()
  cancelReasonText!: string;

  /** 並列登録する他施設 */
  @Prop({ required: true })
  targetFacilities!: Array<TargetFacility>;

  /** 利用者状態選択肢 */
  options: Array<{ text: string; value: ResidentStatus }> = [
    { text: "ステップをお選びください", value: ResidentStatus.UNKNOWN },
    { text: "入所申込み", value: ResidentStatus.APPLICATION },
    { text: "申込み受理", value: ResidentStatus.RECEIVED_APPLICATION },
    { text: "判定済", value: ResidentStatus.COMPLETE_APPLICATION },
    { text: "入所意向確認", value: ResidentStatus.CONFIRM_ADMISSION },
    { text: "最終判定中", value: ResidentStatus.FINAL_JUDGE },
    { text: "最終判定済", value: ResidentStatus.COMPLETE_FINAL_JUDGE },
    { text: "入所手続き", value: ResidentStatus.ADMISSION_PROCEDURE },
    { text: "取り下げ", value: ResidentStatus.CANCELED }
  ];

  /** バリデーションアラート表示可否 */
  showValidationAlert: boolean = false;

  /** 部屋ID */
  roomId: number | null = null;
  /** 部屋セレクトボックス用項目 */
  roomOptions: Array<{ value: number | string; label: string }> = [];
  /** グループ */
  groupId: string | null = null;
  /** グループセレクトボックス用項目 */
  residentGroupOptions: Array<{ value: number | string; label: string }> = [];
  /** 保険者番号 */
  insuredNumber: string = "";
  /** 介護保険被保険者番号 */
  careInsuranceInsuredNumber: string = "";
  /** 入所日 */
  inDatetime: number | null = null;
  /** 入所日(日) */
  inDatetimeDate: string = "";
  /** 入所日(時) */
  inDatetimeTime: string = "";
  /** 入所送迎時間 */
  pickUpDatetime: string | null = null;
  /** 入所送迎時間(時) */
  pickUpDatetimeTime: string = "";
  /** 退所日 */
  outDatetime: number | null = null;
  /** 退所日(日) */
  outDatetimeDate: string = "";
  /** 退所日(時) */
  outDatetimeTime: string = "";
  /** 退所送迎時間 */
  dropOffDatetime: string | null = null;
  /** 退所送迎時間(時) */
  dropOffDatetimeTime: string = "";

  /** 取下げ理由 */
  cancelReason: CancelReason | null = null;
  /** 取下げ理由選択肢 */
  cancelReasonOptions: Array<{ value: number | string; label: string }> = [
    { label: "ご逝去", value: CancelReason.PASSING_AWAY },
    { label: "他施設に入所", value: CancelReason.ANOTHER_FACILITY },
    { label: "その他（自由記述）", value: CancelReason.OTHER }
  ];

  errorMessage: string = "";

  get _value(): boolean {
    return this.value;
  }

  set _value(newValue: boolean) {
    this.$emit("input", newValue);
    return;
  }

  get _changedStatus(): ResidentStatus {
    return this.changedStatus;
  }

  set _changedStatus(newValue: ResidentStatus) {
    this.$emit("update:changedStatus", newValue);
    return;
  }

  get _cancelReasonText(): string {
    return this.cancelReasonText;
  }

  set _cancelReasonText(newValue: string) {
    this.$emit("update:cancelReasonText", newValue);
    return;
  }

  /**
   * ### vue lifecycle created
   */
  created() {
    CustomValidate.addCustomValidate(this.$validator);
    return;
  }

  /**
   * ### vue lifecycle created
   */
  public async mounted(): Promise<void> {
    await this.createView();
    return;
  }

  /**
   * ### 画面情報作成
   *
   * - 部屋の選択肢を作成する
   * - 利用者グループの選択肢を作成する
   * - 入所日の初期値を作成する
   * - 入所送迎時間の初期値を作成する
   * - 退所予定日の初期値を作成する
   * - 退所送迎時間の初期値を作成する
   * - 家族連絡サービスを使用している場合のみ、変更後ステップに「利用者」を追加する
   */
  public async createView(): Promise<void> {
    const rooms: Array<RoomGetModel> = await this.getRooms();
    const roomOptions: Array<{ value: number; label: string }> = this.createRoomOptions(rooms);
    this.roomOptions = roomOptions;

    const residentGroups: Array<ResidentGroupsGetModel> = await this.residentGroupsService.gets(userStore);
    const residentGroupOptions: Array<{ value: number; label: string }> = this.createResidentGroupOptions(residentGroups);
    this.residentGroupOptions = residentGroupOptions;

    this.initialize();

    if (userStore.facility.useFamilyService) {
      this.options.push({ text: "利用者", value: ResidentStatus.RESIDENT });
    }

    return;
  }

  public initialize(): void {
    this.roomId = null;
    this.groupId = null;

    const inDatetimeDate: string = this.$dateUtil.convertDateToString(new Date(), "yyyy-mm-dd");
    const inDatetimeTime: string = "10:00:00";
    this.inDatetime = null;
    this.inDatetimeDate = inDatetimeDate;
    this.inDatetimeTime = inDatetimeTime;
    this.pickUpDatetimeTime = "10:00:00";

    const outDatetimeDate: string = this.$dateUtil.convertDateToString(new Date(), "yyyy-mm-dd");
    const outDatetimeTime: string = "10:00:00";
    this.outDatetime = null;
    this.outDatetimeDate = outDatetimeDate;
    this.outDatetimeTime = outDatetimeTime;
    this.dropOffDatetimeTime = "10:00:00";

    this.showValidationAlert = false;

    return;
  }

  /**
   * ### 部屋取得
   */
  public async getRooms(): Promise<Array<RoomGetModel>> {
    const response: Array<RoomGetModel> = await this.roomsService.gets(userStore);
    return response;
  }

  public createRoomOptions(rooms: Array<RoomGetModel>): Array<{ value: number; label: string }> {
    let roomOptions: Array<{ value: number; label: string }> = [];
    for (const room of rooms) {
      roomOptions.push({
        value: room.id,
        label: `${room.name}（${this.getUseTypeName(room.useType)}・${this.getRoomTypeName(room.roomType)}）`
      });
    }

    return roomOptions;
  }

  public getUseTypeName(useType: number): string {
    if (useType == UseType.UNMANAGED) {
      return "空床管理対象外";
    } else if (useType == UseType.NORMAL_STAY) {
      return "入居用部屋";
    } else if (useType == UseType.SHORT_STAY) {
      return "ショートステイ";
    } else {
      return "";
    }
  }

  public getRoomTypeName(roomType: number): string {
    if (roomType == RoomType.UNMANAGED) {
      return "空床管理対象外";
    } else if (roomType == RoomType.SINGLE) {
      return "個室";
    } else if (roomType == RoomType.DOUBLE) {
      return "２人部屋（夫婦部屋）";
    } else if (roomType == RoomType.MULTI_MEN) {
      return "多床室（男性）";
    } else if (roomType == RoomType.MULTI_WOMEN) {
      return "多床室（女性）";
    } else {
      return "";
    }
  }

  /**
   * ### グループセレクトボックス用項目を作成する
   *
   * - アカウントの権限に応じて選択肢をマスキングする
   */
  public createResidentGroupOptions(residentGroups: Array<ResidentGroupsGetModel>): Array<{ value: number; label: string }> {
    let residentGroupOptions: Array<{ value: number; label: string }> = [];

    const allowedGroupsMap: Map<number, number> = new Map<number, number>();
    for (const allowedGroup of userStore.loginUser.permissions.residentGroups) {
      allowedGroupsMap.set(allowedGroup, allowedGroup);
    }
    for (const group of residentGroups) {
      if (allowedGroupsMap.has(group.id)) {
        residentGroupOptions.push({
          value: group.id,
          label: group.name
        });
      }
    }

    return residentGroupOptions;
  }

  /**
   * ### 状態と状態名を変換する
   */
  public convertStatusName(status: ResidentStatus): string {
    let statusName: string = "";

    switch (status) {
      case ResidentStatus.REGISTERD:
        statusName = "登録者";
        break;
      case ResidentStatus.CANCELED:
        statusName = "取り下げ";
        break;
      case ResidentStatus.TEMPORARILY:
        statusName = "一時保存";
        break;
      case ResidentStatus.APPLICATION:
        statusName = "入所申込み";
        break;
      case ResidentStatus.RECEIVED_APPLICATION:
        statusName = "申込み受理";
        break;
      case ResidentStatus.COMPLETE_APPLICATION:
        statusName = "判定済";
        break;
      case ResidentStatus.CONFIRM_ADMISSION:
        statusName = "入所意向確認";
        break;
      case ResidentStatus.FINAL_JUDGE:
        statusName = "最終判定中";
        break;
      case ResidentStatus.COMPLETE_FINAL_JUDGE:
        statusName = "最終判定済";
        break;
      case ResidentStatus.ADMISSION_PROCEDURE:
        statusName = "入所手続き";
        break;
      case ResidentStatus.RESIDENT:
        statusName = "利用者";
        break;
      default:
        statusName = "";
        break;
    }

    return statusName;
  }

  public async ok(bvModalEvent: any): Promise<void> {
    bvModalEvent.preventDefault();

    // 変更後ステップが未入力の場合エラーとして扱う
    if (this.changedStatus == ResidentStatus.UNKNOWN) {
      this.errorMessage = "変更後のステップを選択してください";
    }
    // 変更後ステップが利用者の場合、バリデーションを実施してOKであれば利用者として更新する
    else if (this.changedStatus == ResidentStatus.RESIDENT) {
      const validationResult: boolean = await this.$validator.validateAll();
      const inDateTimeValidationResult: boolean = await this.validateInDateTime();

      if (validationResult && inDateTimeValidationResult) {
        this.showValidationAlert = false;
        this.$emit("update:cancelReasonText", null); // 取下げでいないので取下げ理由を空にする
        const updateResidentModel: UpdateResidentModel = this.createUpdateResidentModel();
        this.$emit("ok", updateResidentModel);
      } else {
        this.showValidationAlert = true;
      }
    }
    // 変更後ステップが取り下げの場合、バリデーションを実施してOKであれば取り下げ理由を付与して更新する
    else if (this.changedStatus == ResidentStatus.CANCELED) {
      const validationResult: boolean = await this.$validator.validateAll();

      if (validationResult) {
        this.showValidationAlert = false;
        const cancelReason: string = this.createCancelReason();
        this.$emit("update:cancelReasonText", cancelReason);
        this.$emit("ok");
      } else {
        this.showValidationAlert = true;
      }
    }
    // 変更後ステップが利用者以外の場合、利用者状態を更新する
    else {
      this.$emit("update:cancelReasonText", null); // 取下げでいないので取下げ理由を空にする
      this.$emit("ok", null);
    }

    return;
  }

  /** 入所日時・退所日時のチェックは自前で行う */
  public async validateInDateTime(): Promise<boolean> {
    this.$validator.errors.remove("in-date");
    if (this.inDatetime && this.outDatetime) {
      if (this.outDatetime < this.inDatetime) {
        this.$validator.errors.add({ field: "in-date", msg: "入所日が退所日より未来です" });
        return false;
      } else {
        return true;
      }
    }
    return true;
  }

  public createUpdateResidentModel(): UpdateResidentModel {
    // 入所送迎時間、退所送迎時間は入所日と退所送迎日の日付を使用する
    let pickUpDatetime: number | null = null;
    if (this.inDatetime && this.pickUpDatetime) {
      const pickUpDate: string = this.$dateUtil.convertNumberToString(this.inDatetime, "yyyymmdd");
      const pickUpTime: string = this.$dateUtil.replaceNonNumeric(this.pickUpDatetime); // time format is hh:MM:ss
      pickUpDatetime = Number(`${pickUpDate}${pickUpTime}000`);
    }
    let dropOffDatetime: number | null = null;
    if (this.outDatetime && this.dropOffDatetime) {
      const dropOffDate: string = this.$dateUtil.convertNumberToString(this.outDatetime, "yyyymmdd");
      const dropOffTime: string = this.$dateUtil.replaceNonNumeric(this.dropOffDatetime); // time format is hh:MM:ss
      dropOffDatetime = Number(`${dropOffDate}${dropOffTime}000`);
    }

    // 入所・退所情報が1つでもあればそれを配列に格納し、なければ空配列を格納する
    let inAndOutDatetimes = [];
    if (this.inDatetime || this.outDatetime || pickUpDatetime || dropOffDatetime) {
      inAndOutDatetimes.push({
        inDatetime: this.inDatetime ? this.inDatetime : null,
        pickUpDatetime: pickUpDatetime ? pickUpDatetime : null,
        outDatetime: this.outDatetime ? this.outDatetime : null,
        dropOffDatetime: dropOffDatetime ? dropOffDatetime : null,
        roomId: this.roomId as number
      });
    }

    return {
      groupId: Number(this.groupId),
      insuredNumber: this.insuredNumber,
      careInsuranceInsuredNumber: this.careInsuranceInsuredNumber,
      roomId: this.roomId as number,
      inAndOutDatetimes: inAndOutDatetimes
    };
  }

  /** 取下げ理由のテキストを返す */
  createCancelReason(): string {
    switch (this.cancelReason) {
      case CancelReason.PASSING_AWAY: {
        return "ご逝去";
      }
      case CancelReason.ANOTHER_FACILITY: {
        return "他施設に入所";
      }
      case CancelReason.OTHER: {
        return this.cancelReasonText;
      }
    }
    return "";
  }

  public cancel(): void {
    this.initialize();
    this.$emit("cancel");
    return;
  }
}

export interface TargetFacility {
  /** 施設ID */
  id: number;
  /** 施設名 */
  name: string;
  /** 利用者ID */
  residentId: number;
  /** 選択したか否か */
  checked: boolean;
}

export enum CancelReason {
  /** ご逝去 */
  PASSING_AWAY = "passingAway",
  /** 他施設に入所 */
  ANOTHER_FACILITY = "anotherFacility",
  /** その他（自由記述） */
  OTHER = "other"
}
</script>
